import { IllegalActionError, YARD, isHome } from "./types.js";
import { legalMoves } from "./moves.js";
import { assertValidState, isPlayableSeat } from "./validation.js";
export function applyRoll(state, dice, receipt, now) {
    assertValidState(state);
    if (state.phase !== "roll")
        throw new IllegalActionError("not_awaiting_roll", "A roll is not expected now");
    if (!isPlayableSeat(state, state.turn))
        throw new IllegalActionError("invalid_turn", "The current player cannot roll");
    if (!Array.isArray(dice) || dice.length !== 2 || !dice.every((d) => Number.isInteger(d) && d >= 1 && d <= 6))
        throw new IllegalActionError("invalid_die", "Dice must be 1 through 6");
    if (!receipt.trim())
        throw new IllegalActionError("invalid_receipt", "A roll receipt is required");
    const events = [{ t: "roll", seat: state.turn, die: dice[0], dice, receipt }];
    const isDouble = dice[0] === dice[1];
    // Update consecutive doubles for current player
    const currentP = state.players.find((p) => p.seat === state.turn);
    const currentDoubles = currentP?.consecutiveDoubles ?? 0;
    const newConsecutiveDoubles = isDouble ? currentDoubles + 1 : 0;
    const updatedPlayers = state.players.map((p) => p.seat === state.turn ? { ...p, consecutiveDoubles: newConsecutiveDoubles } : p);
    const sixStreak = (dice[0] === 6 && dice[1] === 6) ? state.sixStreak + 2 : (dice[0] === 6 || dice[1] === 6) ? state.sixStreak + 1 : 0;
    const rolled = {
        ...state,
        die: dice[0],
        dice: [...dice],
        sixStreak,
        rollNonce: state.rollNonce + 1,
        deadlineTs: now + state.rules.turnSeconds * 1000,
        lastRoll: { nonce: state.rollNonce, dice: [...dice], receipt, at: now },
        stats: increment(state, "rolls", state.turn, dice.length),
        players: updatedPlayers,
        lastMoved: { ...state.lastMoved },
        turnRewardPending: isDouble ? "doubles" : null,
    };
    // Rule: Check triple-doubles penalty (3 consecutive doubles during the same turn sequence)
    if (isDouble && newConsecutiveDoubles >= 3) {
        // Apply penalty: send the most recently moved piece (if any) back to yard
        const lastMovedId = state.lastMoved[state.turn];
        let penalizedTokens = rolled.tokens;
        const eventsPenalty = [];
        if (lastMovedId) {
            const movedToken = penalizedTokens.find((t) => t.id === lastMovedId);
            if (movedToken && movedToken.cell !== YARD && !isHome(movedToken.cell)) {
                penalizedTokens = penalizedTokens.map((t) => t.id === lastMovedId ? { ...t, cell: YARD } : t);
                eventsPenalty.push({ t: "move", tokenId: lastMovedId, from: movedToken.cell, to: YARD });
            }
        }
        // Reset consecutive doubles to 0 for this player
        const clearedPlayers = rolled.players.map((p) => p.seat === state.turn ? { ...p, consecutiveDoubles: 0 } : p);
        // End turn and pass to next player
        const penalizedState = {
            ...rolled,
            tokens: penalizedTokens,
            players: clearedPlayers,
            die: null,
            dice: [],
            sixStreak: 0,
            turnRewardPending: null,
            phase: "roll",
        };
        const passed = passTurn(penalizedState);
        events.push(...eventsPenalty);
        events.push({ t: "pass", color: state.turn, reason: "triple_doubles" });
        if (passed.phase === "over")
            events.push({ t: "gameOver", standings: standingsFor(passed) });
        return { state: passed, moves: [], forfeited: true, events };
    }
    let staged = { ...rolled, phase: "move" };
    let moves = legalMoves(staged, staged.die ?? 0);
    // If no moves for first die, check if player can move for second die
    if (!moves.length && dice.length > 1) {
        const secondDieMoves = legalMoves({ ...staged, die: dice[1] }, dice[1]);
        if (secondDieMoves.length > 0) {
            staged = {
                ...staged,
                die: dice[1],
                dice: [dice[1], dice[0]],
            };
            moves = secondDieMoves;
        }
        else {
            // No moves for either die
            // If rolled doubles (1st or 2nd), grant extra turn (do not pass)!
            if (isDouble && newConsecutiveDoubles < 3 && state.rules.extraTurnOnDoubles !== false) {
                events.push({ t: "pass", color: state.turn, reason: "no_moves" });
                events.push({ t: "extraTurn", seat: state.turn, reason: "doubles" });
                const extraTurnState = {
                    ...rolled,
                    die: null,
                    dice: [],
                    phase: "roll",
                    sixStreak: 0,
                    turnRewardPending: null,
                };
                return { state: extraTurnState, moves: [], forfeited: false, events };
            }
            // Not doubles and no moves: pass turn to next player
            const clearedPlayers = rolled.players.map((p) => p.seat === state.turn ? { ...p, consecutiveDoubles: 0 } : p);
            const passed = passTurn({ ...staged, players: clearedPlayers, die: null, dice: [], sixStreak: 0, phase: "roll", turnRewardPending: null });
            events.push({ t: "pass", color: state.turn, reason: "no_moves" });
            if (passed.phase === "over")
                events.push({ t: "gameOver", standings: standingsFor(passed) });
            return { state: passed, moves: [], forfeited: false, events };
        }
    }
    else if (!moves.length) {
        // Single die with no moves
        const clearedPlayers = rolled.players.map((p) => p.seat === state.turn ? { ...p, consecutiveDoubles: 0 } : p);
        const passed = passTurn({ ...staged, players: clearedPlayers, die: null, dice: [], sixStreak: 0, phase: "roll", turnRewardPending: null });
        events.push({ t: "pass", color: state.turn, reason: "no_moves" });
        if (passed.phase === "over")
            events.push({ t: "gameOver", standings: standingsFor(passed) });
        return { state: passed, moves: [], forfeited: false, events };
    }
    return { state: staged, moves, forfeited: false, events };
}
export function applyMove(state, tokenId, now, preferredDie) {
    assertValidState(state);
    if (state.phase !== "move" || (state.die === null && (!state.dice || state.dice.length === 0)))
        throw new IllegalActionError("not_awaiting_move", "A token move is not expected now");
    if (!isPlayableSeat(state, state.turn))
        throw new IllegalActionError("invalid_turn", "The current player cannot move");
    let activeState = state;
    if (preferredDie && state.dice && state.dice.includes(preferredDie) && state.die !== preferredDie) {
        const otherDice = [...state.dice];
        const pickedIdx = otherDice.indexOf(preferredDie);
        if (pickedIdx >= 0) {
            otherDice.splice(pickedIdx, 1);
            activeState = {
                ...state,
                die: preferredDie,
                dice: [preferredDie, ...otherDice],
            };
        }
    }
    let move = legalMoves(activeState, activeState.die ?? (activeState.dice[0] ?? 0)).find((candidate) => candidate.tokenId === tokenId);
    // If token cannot move with current active die, check if it can move with second die
    if (!move && activeState.dice && activeState.dice.length > 1) {
        const secondDie = activeState.dice[1];
        const secondMoves = legalMoves(activeState, secondDie);
        const secondMove = secondMoves.find((candidate) => candidate.tokenId === tokenId);
        if (secondMove) {
            activeState = {
                ...activeState,
                die: secondDie,
                dice: [secondDie, activeState.dice[0]],
            };
            move = secondMove;
        }
    }
    if (!move)
        throw new IllegalActionError("illegal_move", "This token cannot make the pending move");
    return applyLegalMove(activeState, move, now);
}
/** Validates a precomputed move again so callers cannot bypass rule validation. */
export function applyLegalMove(state, move, now) {
    assertValidState(state);
    if (state.phase !== "move" || (state.die === null && (!state.dice || state.dice.length === 0)))
        throw new IllegalActionError("not_awaiting_move", "A token move is not expected now");
    const legal = legalMoves(state, state.die ?? (state.dice[0] ?? 0)).find((candidate) => candidate.tokenId === move.tokenId);
    if (!legal || !sameMove(legal, move))
        throw new IllegalActionError("illegal_move", "The supplied move is not legal for this board state");
    return applyValidatedMove(state, legal, now);
}
function applyValidatedMove(state, move, now) {
    const events = [{ t: "move", tokenId: move.tokenId, from: move.from, to: move.to }];
    const tokens = state.tokens.map((token) => {
        if (token.id === move.tokenId)
            return { ...token, cell: move.to };
        if (move.captures.includes(token.id))
            return { ...token, cell: YARD };
        return token;
    });
    if (move.captures.length)
        events.push({ t: "capture", tokenIds: move.captures, by: state.turn });
    if (move.finishes)
        events.push({ t: "finish", tokenId: move.tokenId, color: state.turn });
    // Update lastMoved for this player
    const lastMoved = { ...state.lastMoved, [state.turn]: move.tokenId };
    // Track turn rewards (capture or reaching home)
    let turnRewardPending = state.turnRewardPending ?? null;
    if (move.captures.length > 0 && state.rules.extraTurnOnCapture !== false) {
        turnRewardPending = turnRewardPending ?? "capture";
    }
    if (move.finishes && state.rules.extraTurnOnHome !== false) {
        turnRewardPending = turnRewardPending ?? "home";
    }
    // Consume current die and advance dice array
    const remainingDice = (state.dice && state.dice.length > 0) ? state.dice.slice(1) : [];
    let next = {
        ...state,
        tokens,
        die: remainingDice[0] ?? null,
        dice: remainingDice,
        phase: remainingDice.length > 0 ? "move" : "roll",
        deadlineTs: now + state.rules.turnSeconds * 1000,
        stats: incrementMoveStatistics(state, state.turn, move),
        lastMoved,
        turnRewardPending,
    };
    const completed = completedSeats(next, state.turn);
    if (completed.length && !completed.some((seat) => next.finished.includes(seat))) {
        for (const seat of completed) {
            if (!next.finished.includes(seat)) {
                next = { ...next, finished: [...next.finished, seat] };
                events.push({ t: "playerDone", color: seat, place: next.finished.length });
            }
        }
    }
    // If the current player completed all tokens home, match won or turn passes
    if (completed.includes(state.turn)) {
        const clearedPlayers = next.players.map((p) => p.seat === state.turn ? { ...p, consecutiveDoubles: 0 } : p);
        next = passTurn({ ...next, players: clearedPlayers, sixStreak: 0, turnRewardPending: null });
    }
    const active = playableSeats(next);
    const remaining = active.filter((seat) => !next.finished.includes(seat));
    const matchWon = next.rules.suddenDeath
        ? next.finished.length > 0
        : active.length === 1
            ? next.finished.length === 1
            : remaining.length <= 1;
    if (matchWon) {
        const standings = standingsFor(next);
        next = { ...next, phase: "over", die: null, dice: [], turnRewardPending: null };
        events.push({ t: "gameOver", standings });
        return { state: next, extraTurn: false, events };
    }
    // Check if remaining dice have legal moves
    if (next.dice && next.dice.length > 0) {
        const nextMoves = legalMoves(next, next.die ?? 0);
        if (nextMoves.length > 0) {
            // More moves available with the second die -> stay in move phase
            assertValidState(next);
            return { state: next, extraTurn: false, events };
        }
        // No legal moves for remaining dice -> remaining dice are discarded
        next = { ...next, dice: [], die: null };
    }
    // All dice have been played or exhausted! Evaluate extra turn.
    const hasExtraTurn = !completed.includes(state.turn) && Boolean(next.turnRewardPending);
    const extraTurnReason = hasExtraTurn ? next.turnRewardPending : null;
    if (hasExtraTurn) {
        // Grant exactly ONE extra turn (do not pass turn)
        events.push({ t: "extraTurn", seat: state.turn, reason: extraTurnReason });
        next = {
            ...next,
            dice: [],
            die: null,
            phase: "roll",
            sixStreak: 0,
            turnRewardPending: null,
        };
        assertValidState(next);
        return { state: next, extraTurn: true, extraTurnReason, events };
    }
    // No extra turn: reset consecutiveDoubles and pass turn to next player
    const clearedPlayers = next.players.map((p) => p.seat === state.turn ? { ...p, consecutiveDoubles: 0 } : p);
    next = passTurn({ ...next, players: clearedPlayers, sixStreak: 0, turnRewardPending: null });
    assertValidState(next);
    return { state: next, extraTurn: false, events };
}
/** Deterministic timeout transition used by realtime hosts. */
export function applyTimeout(state, now) {
    assertValidState(state);
    if (state.phase === "over")
        throw new IllegalActionError("game_over", "The match has already finished");
    const timedOut = state.turn;
    const players = state.players.map((player) => player.seat === timedOut ? { ...player, consecutiveTimeouts: player.consecutiveTimeouts + 1, consecutiveDoubles: 0 } : player);
    const passed = passTurn({ ...state, players, die: null, dice: [], phase: "roll", sixStreak: 0, deadlineTs: now, turnRewardPending: null });
    return { state: passed, extraTurn: false, events: [{ t: "pass", color: timedOut, reason: "timeout" }] };
}
export function passTurn(state) {
    const seats = playableSeats(state).filter((seat) => !state.finished.includes(seat));
    if (!seats.length)
        return { ...state, die: null, dice: [], phase: "over", sixStreak: 0, turnRewardPending: null };
    if (seats.length === 1) {
        const onlySeat = seats[0] ?? state.turn;
        const hasOtherConfiguredSeat = state.players.some((player) => player.seat !== onlySeat);
        return { ...state, turn: onlySeat, die: null, dice: [], phase: hasOtherConfiguredSeat ? "over" : "roll", sixStreak: 0, turnRewardPending: null };
    }
    const currentIndex = seats.indexOf(state.turn);
    const next = seats[(currentIndex + 1 + seats.length) % seats.length] ?? seats[0] ?? state.turn;
    return { ...state, turn: next, die: null, dice: [], phase: "roll", sixStreak: 0, turnRewardPending: null };
}
function playableSeats(state) {
    return state.players
        .filter((player) => isPlayableSeat(state, player.seat))
        .map((player) => player.seat)
        .sort((a, b) => a - b);
}
function completedSeats(state, seat) {
    const current = state.players.find((player) => player.seat === seat);
    if (!current)
        return [];
    const required = state.rules.teams && current.team !== null
        ? state.players.filter((player) => player.team === current.team).map((player) => player.seat)
        : [seat];
    const done = required.every((color) => state.tokens.filter((token) => token.color === color).every((token) => isHome(token.cell)));
    return done ? required : [];
}
function standingsFor(state) {
    return [...state.finished, ...playableSeats(state).filter((seat) => !state.finished.includes(seat))];
}
function increment(state, key, color, amount = 1) {
    return { ...state.stats, [key]: { ...state.stats[key], [color]: state.stats[key][color] + amount } };
}
function incrementMoveStatistics(state, color, move) {
    let stats = increment(state, "moves", color);
    if (move.captures.length)
        stats = { ...stats, captures: { ...stats.captures, [color]: stats.captures[color] + move.captures.length } };
    if (move.finishes)
        stats = { ...stats, homes: { ...stats.homes, [color]: stats.homes[color] + 1 } };
    return stats;
}
function sameMove(first, second) {
    return first.tokenId === second.tokenId
        && first.from === second.from
        && first.to === second.to
        && first.captures.length === second.captures.length
        && first.captures.every((id, index) => id === second.captures[index])
        && first.path.length === second.path.length
        && first.path.every((cell, index) => cell === second.path[index]);
}
